import { Children, isValidElement, useState, type HTMLAttributes, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function textLength(node: ReactNode): number {
  return Children.toArray(node).reduce<number>((length, child) => {
    if (typeof child === "string" || typeof child === "number") return length + String(child).length;
    if (isValidElement<{ children?: ReactNode }>(child)) return length + textLength(child.props.children);
    return length;
  }, 0);
}

function ExpandableParagraph({ children, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = textLength(children) > 420;
  return (
    <div className="my-2">
      <p {...props} className={`${props.className ?? ""} ${canExpand && !expanded ? "line-clamp-5" : ""}`}>
        {children}
      </p>
      {canExpand && (
        <button className="mt-1 text-xs text-accent underline" onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export function MarkdownContent({ markdown, className = "" }: { markdown: string; className?: string }) {
  return (
    <div className={`min-w-0 space-y-2 break-words text-sm leading-relaxed text-text/90 [&_a]:text-accent [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-accent/50 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-border/40 [&_code]:px-1 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-bg [&_pre]:p-3 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ExpandableParagraph,
          img: ({ src, alt }) => (
            <img src={src} alt={alt ?? ""} loading="lazy" className="my-3 h-auto max-w-full rounded-lg object-contain" />
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
